import { t } from 'i18next';

import { env } from '@/env';
import { TranslationKey } from '@/i18n';

enum routeMatches {
  AdminPath,
  AdminRootPath,
  CategoriesPath,
  CompliancePath,
  ComplianceDocumentPath,
  DetailsPath,
  DocumentPath,
  ExplorePath,
  LeadershipPath,
  LeadershipDocumentPath,
  ReportsPath,
  ReportsCompliancePlansPath,
  ReportsPlansPath,
  ReportsPlansViewPath,
  ReportsTeamMembersPath,
  ReportsTeamMembersViewPath,
  ReportsTeamMemberProgressViewPath,
  RootPath,
  SearchPath,
  SettingsPath,
  SignalPath,
  TeamMemberPlan,
  TrainingAssignedPath,
  TrainingBuildPath,
  TrainingBuildQuizzesPath,
  TrainingEditQuizzesPath,
  TrainingModePath,
  TrainingMyPlansPath,
  TrainingPath,
  TrainingPlanEditPath,
  TrainingQuizzesPath,
  TrainingTeamMemberProgressPath,
  TrainingTeamMembersPath,
  TrainingTeamPath,
  TrainingToCompletePath,
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class Index {
  static get APP_VERSION() {
    return env.REACT_APP_VERSION;
  }

  // This value should be incremented when there is a breaking change to the Redux data schema.
  // This ensures user's old persisted Redux stores are cleared, to prevent errors.
  // If two breaking changes are made since the last production release, this number only needs to be incremented once.
  // Examples: changing a pre-existing property from a string to an array is a breaking change. Adding new properties to Redux is not.
  static readonly REDUX_STORE_VERSION = 1;

  static readonly BASE_PATH = '';

  static readonly DEV = 'dev';

  static readonly TEST = 'test';

  static readonly PROD = 'prod';

  static get AMPLITUDE_API_KEY() {
    return env.REACT_APP_AMPLITUDE_API_KEY;
  }

  /**
   * The below two constants are sorted by highest order of permissions
   * See https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/3781493005/Application+Permissions
   */
  static get USER_PERMISSIONS() {
    return {
      ADMIN: 'ADMIN',
      STAKEHOLDER: 'STAKEHOLDER', // Support Center User
      OPERATOR: 'OPERATOR',
      LEADER: 'LEADER',
      TRAINER: 'TRAINER',
      LOGIN: 'LOGIN', // Team Member
      LICENSEESTAFF: 'LICENSEESTAFF',
    };
  }

  static get USER_RESTRICTIONS() {
    return {
      GREAT_BRITAIN_USER: 'GREAT_BRITAIN_USER',
    };
  }

  static get USER_PERMISSION_NAMES() {
    return {
      ADMIN: 'Admin',
      STAKEHOLDER: 'Stakeholder', // Support Center User
      OPERATOR: 'Operator',
      LEADER: 'Leader',
      TRAINER: 'Trainer',
      LOGIN: 'Team Member',
    };
  }

  static readonly APPLICATION_SYSTEM_TAG = 'P20';

  static get ENV() {
    return env.REACT_APP_ENV.toLowerCase() || this.DEV;
  }

  static readonly DEFAULT_PAGING_SIZE = 12;

  static get PATHWAY_CDN_URL() {
    return env.REACT_APP_PATHWAY_CDN_URL;
  }

  static get LAUNCH_DARKLY_CLIENT_ID() {
    return env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;
  }

  static get RN_MESSAGE_TYPES() {
    return {
      PRINT: 'print',
      BACK: 'back',
    };
  }

  static get PATHWAY_API() {
    return {
      ASSIGN_PATH_NAME: 'assign',
      ASSIGNED_PATH_NAME: 'assigned',
      BASE_URL: env.REACT_APP_PATHWAY_API_URL,
      BASE_XP_URL: env.REACT_APP_PATHWAY_XP_API_URL,
      BINARY_PATH_NAME: 'binary',
      BUILD_PATH_NAME: 'build',
      CATEGORIES_PATH_NAME: 'categories',
      CONTENT_PATH_NAME: 'content',
      COURSES_PATH_NAME: 'courses',
      DISABLE_PATH_NAME: 'disable',
      DOCUMENTS_PATH_NAME: 'documents',
      DUE_DATE_PATH_NAME: 'dueDate',
      ENABLE_PATH_NAME: 'enable',
      HEARTBEAT_PATH_NAME: 'heartbeat',
      ICONS_PATH_NAME: 'icons',
      LIKE_PATH_NAME: 'like',
      LIST_PATH_NAME: 'list',
      OPERATOR_ID_PATH_NAME: 'operatorId',
      OPERATORS_PATH_NAME: 'operators',
      PLAN_PATH_NAME: 'plan',
      PLANS_PATH_NAME: 'plans',
      PREFERENCES_PATH_NAME: 'preferences',
      QUIZ_PATH_NAME: 'quiz',
      REPORT_PATH_NAME: 'report',
      REPORTS_PATH_NAME: 'reports',
      REPORTS_TEAM_MEMBER_PATH_NAME: 'teamMember',
      REPORTS_TEAM_MEMBERS_PATH_NAME: 'teamMembers',
      RESET_PATH_NAME: 'reset',
      SEARCH_PATH_NAME: 'search',
      SERVICE_PATH_NAME: 'service',
      SETTINGS_PATH_NAME: 'settings',
      SQSSO_PATH_NAME: 'sqsso',
      STATUS_PATH_NAME: 'status',
      STEPS_PATH_NAME: 'steps',
      SUBCATEGORIES_PATH_NAME: 'subcategories',
      TEAM_PATH_NAME: 'team',
      TEAM_MEMBERS_PATH_NAME: 'team-members',
      TO_COMPLETE_PATH_NAME: 'toComplete',
      TRAINING_CHECKLIST_PATH_NAME: 'checklist',
      TRAINING_TEMPLATES_PATH_NAME: 'templates',
      TRAINING_PATH_NAME: 'training',
      TRANSLATE_PATH_NAME: 'translate',
      USERS_PATH_NAME: 'users',
      V2_PATH_NAME: 'v2',
      XP_PATH_NAME: 'xp',
    };
  }

  static get LOCATION_TYPES() {
    return {
      CFA: 'CFA',
      COR: 'COR',
      FRA: 'FRA',
      LIC: 'LIC',
      LBM: 'LBM',
      DWH: 'DWH',
      TG: 'TG',
    };
  }

  static get USER_AUDIENCE() {
    return {
      LICENSEE: 'LICENSEE_AUDIENCE',
      OPERATOR: 'OPERATOR_AUDIENCE',
    };
  }

  static get TRAINING_PLANS() {
    return {
      COMPLETE: 'COMPLETE',
      COMPLETED: 'COMPLETED',
      INCOMPLETE: 'INCOMPLETE',
      IN_PROGRESS: 'IN_PROGRESS',
      NOT_COMPLETED: 'NOT_COMPLETED',
      NOT_STARTED: 'NOT_STARTED',
      UNASSIGNED: 'UNASSIGNED',
    };
  }

  static get TRAINING_PLANS_STATUSES() {
    return {
      COMPLETED: 'completed',
      IN_PROGRESS: 'inProgress',
      PASSED: 'passed',
      NOT_STARTED: 'notStarted',
      UNASSIGNED: 'unassigned',
    } as const;
  }

  static get PLANS_SORT_OPTIONS() {
    return {
      A2Z: 'A2Z',
      Z2A: 'Z2A',
      DUE_DATE: 'DUE DATE',
      NEWEST: 'NEWEST',
      OLDEST: 'OLDEST',
      START_DATE: 'START DATE',
    };
  }
  //{ BACK_OF_HOUSE: string; COMPLIANCE: string; FRONT_OF_HOUSE: string; GENERAL: string; HOSPITALITY: string; ONBOARDING: string; }
  static get ALL_PLAN_CATEGORY_OBJECTS() {
    return (
      Object.keys(this.PLAN_CATEGORIES) as (keyof typeof this.PLAN_CATEGORIES)[]
    ).map(key => {
      const id = this.PLAN_CATEGORIES[key];
      const alias = this.PLAN_CATEGORIES[key];
      const color = this.PLAN_CATEGORIES[key];
      return {
        key,
        id,
        alias,
        color,
      };
    });
  }

  static get PLAN_CATEGORIES() {
    return {
      GENERAL: 'general',
      BACK_OF_HOUSE: 'back_of_house',
      COMPLIANCE: 'compliance',
      FRONT_OF_HOUSE: 'front_of_house',
      HOSPITALITY: 'hospitality',
      ONBOARDING: 'onboarding',
      LEADERSHIP: 'leadership',
    };
  }

  static get PLAN_STATUS() {
    return {
      INCLUDE_COMPLETED: 'include_completed',
    };
  }

  static get PLAN_CATEGORY_COLORS() {
    return {
      BACK_OF_HOUSE: '#994878',
      COMPLIANCE: '#249E6B',
      FRONT_OF_HOUSE: '#FFB549',
      GENERAL: '#004F71',
      HOSPITALITY: '#FA9370',
      IS_DISABLED: '#A7ACAF',
      ONBOARDING: '#00635B',
      LEADERSHIP: '#3BB1C8',
    };
  }

  static get PLAN_CATEGORY_ALIAS_TRANSLATIONS() {
    return {
      BACK_OF_HOUSE:
        'TrainingPlans.planCategories.backOfHouse' satisfies TranslationKey as TranslationKey,
      COMPLIANCE:
        'TrainingPlans.planCategories.compliance' satisfies TranslationKey as TranslationKey,
      FRONT_OF_HOUSE:
        'TrainingPlans.planCategories.frontOfHouse' satisfies TranslationKey as TranslationKey,
      GENERAL:
        'TrainingPlans.planCategories.general' satisfies TranslationKey as TranslationKey,
      HOSPITALITY:
        'TrainingPlans.planCategories.hospitality' satisfies TranslationKey as TranslationKey,
      ONBOARDING:
        'TrainingPlans.planCategories.onboarding' satisfies TranslationKey as TranslationKey,
      LEADERSHIP:
        'TrainingPlans.planCategories.leadership' satisfies TranslationKey as TranslationKey,
    };
  }

  static get LEARN_UPON_TRAINING_PLANS() {
    return {
      ATTENDED: 'attended',
      CANCELLED: 'cancelled',
      COMPLETED: 'completed',
      FAILED: 'failed',
      IN_PROGRESS: 'in_progress',
      NO_SHOW: 'no_show',
      NOT_COMPLETED: 'not_completed',
      NOT_STARTED: 'not_started',
      PASSED: 'passed',
      PENDING_REVIEW: 'pending_review',
      NULL: null,
    };
  }

  static get LEARN_UPON_TRAINING_PLAN_STATUSES() {
    return {
      attended: 'attended',
      cancelled: 'cancelled',
      completed: 'completed',
      failed: 'failed',
      in_progress: 'inProgress',
      no_show: 'noShow',
      not_started: 'notStarted',
      passed: 'passed',
      pending_review: 'pendingReview',
    } as const;
  }

  static get COURSES() {
    return {
      ENABLE: 'enable',
      DISABLE: 'disable',
    };
  }

  static get COURSE_TYPES() {
    return {
      COMPLIANCE: 'Compliance',
    };
  }

  static get XP_API_BASE_URL() {
    return env.REACT_APP_XP_API_URL;
  }

  static get XP_API_IMAGE_BASE_URL() {
    if (!env.REACT_APP_XP_API_IMAGE_URL) {
      throw new Error('REACT_APP_XP_API_IMAGE_URL is not defined');
    }
    return env.REACT_APP_XP_API_IMAGE_URL;
  }

  static get APP_ENVIRONMENTS() {
    return {
      DEV: 'development',
      TEST: 'test',
      PROD: 'production',
    };
  }

  static get VIEW_TIME_DEFAULTS() {
    return {
      POLL_TIME_DEFAULT: this.IS_IN_CYPRESS_TEST ? 500 : 30000,
      POLL_TIME_ROUNDUP: this.IS_IN_CYPRESS_TEST ? 100 : 15000,
      DEBOUNCE_TIME_DEFAULT: this.IS_IN_CYPRESS_TEST ? 100 : 1500,
    };
  }

  static get DOCUMENT_COOKIE_REFRESH() {
    return {
      POLL_TIME_DEFAULT: 1000 * 60 * 5, // 1000 ms * 5 min = 5 minutes
    };
  }

  static get QUIZ_RESULTS() {
    return {
      PASS: 'PASS',
      FAIL: 'FAIL',
    };
  }

  static get QUERY_PARAMS() {
    return {
      TRUE: 'true',
      FALSE: 'false',
    };
  }

  static get QUESTION_TEMPLATE_TYPES() {
    return {
      MULTIPLE_CHOICE: {
        id: 'mc',
        templateName: 'TrainingPlans.buildQuizzes.multipleChoice' as const,
        isMultipleChoiceTemplate: true,
      },
      TRUE_OR_FALSE: {
        id: 'tf',
        templateName: 'TrainingPlans.buildQuizzes.trueOrFalse' as const,
        isMultipleChoiceTemplate: false,
      },
    };
  }

  static get DEFAULT_MULTIPLE_CHOICE_QUESTION() {
    return {
      question: {
        en: '',
      },
      answers: [
        {
          answer: {
            en: '',
          },
          correct: false,
        },
        {
          answer: {
            en: '',
          },
          correct: false,
        },
        {
          answer: {
            en: '',
          },
          correct: false,
        },
      ],
    };
  }
  static get DEFAULT_QUIZ_ANSWER() {
    return {
      answer: {
        en: '',
      },
      correct: false,
    };
  }

  static get DEFAULT_TRUE_FALSE_QUESTION() {
    return {
      question: {
        en: '',
      },
      answers: [
        {
          answer: {
            en: 'True',
            es: 'Cierto',
          },
          correct: false,
        },
        {
          answer: {
            en: 'False',
            es: 'Falso',
          },
          correct: false,
        },
      ],
    };
  }

  static get ROUTE_PATH_NAMES() {
    return {
      ADMIN_PATH_NAME: 'admin',
      ALL_PATH_NAME: 'all',
      ASSIGN_PATH_NAME: 'assign',
      ASSIGNED_PATH_NAME: 'assigned',
      BINARY_PATH_NAME: 'binary',
      BUILD_PATH_NAME: 'build',
      CATEGORY_PATH_NAME: 'category',
      COMPLIANCE_PATH_NAME: 'compliance',
      DOCUMENT_PATH_NAME: 'doc',
      EXPLORE_PATH_NAME: 'explore',
      LIST_PATH_NAME: 'list',
      PLAN_PATH_NAME: 'plan',
      PLANS_PATH_NAME: 'plans',
      PROGRESS_PATH_NAME: 'progress',
      QUIZ_PATH_NAME: 'quiz',
      QUIZZES_PATH_NAME: 'quizzes',
      REPORTS_PATH_NAME: 'reports',
      SEARCH_PATH_NAME: 'search',
      SETTINGS_PATH_NAME: 'settings',
      HEADLESS_PATH_NAME: 'headless',
      TEAM_PATH_NAME: 'team',
      TEAM_MEMBERS_PATH_NAME: 'team-members',
      TO_COMPLETE_PATH_NAME: 'toComplete',
      TRAINING_CHECKLIST_PATH_NAME: 'checklist',
      TRAINING_MODE_PATH_NAME: 'trainingMode',
      TRAINING_PATH_NAME: 'training',
      USERS_PATH_NAME: 'users',
    };
  }

  static get ROUTE_PATH_GROUPS() {
    return routeMatches;
  }

  static get NAVBAR_NAMES() {
    return {
      EXPLORE_NAVBAR_NAME: 'Explore',
      PLANS_NAVBAR_NAME: 'Plans',
      REPORTS_NAVBAR_NAME: 'Reports',
    };
  }

  static get KENDRA() {
    return {
      SEARCH_RESULTS: {
        FEEDBACK: {
          RELEVANT: 'RELEVANT',
          NOT_RELEVANT: 'NOT_RELEVANT',
        },
      },
    };
  }

  static get PATHWAY_API_URL() {
    return this.PATHWAY_API.BASE_URL;
  }

  static get PATHWAY_NATIVE_API_URL() {
    return env.REACT_APP_PATHWAY_NATIVE_API_URL;
  }

  static get PATHWAY_API_CONTENT_URL() {
    return `${this.PATHWAY_API.BASE_URL}/${this.PATHWAY_API.CONTENT_PATH_NAME}`;
  }

  static get PATHWAY_CDN_IMG_URL() {
    return `${this.PATHWAY_CDN_URL}/img/`;
  }

  static get PATHWAY_CDN_IMG() {
    return {
      CATEGORIES: `${this.PATHWAY_CDN_IMG_URL}categories/`,
      PROCEDURES: `${this.PATHWAY_CDN_IMG_URL}procedures/`,
      TRAINING: `${this.PATHWAY_CDN_IMG_URL}checklists/`,
    };
  }

  static get TRAINING_MENU_OPTIONS() {
    return {
      CUSTOM_PLAN: 'Custom Plan',
      PLAN_FROM_TEMPLATE_LIBRARY: 'Plan from Template Library',
      PROCEDURE: 'PROCEDURE',
      SECTION: 'SECTION',
      TASK: 'TASK',
    };
  }

  static get LANGUAGE_OPTIONS() {
    // please update getLanguageCode() in utils as languages are added
    return {
      ENGLISH: 'English',
      ESPANOL: 'Español',
      INGLES: 'Ingles',
      SPANISH: 'Spanish',
    };
  }

  static get LANGUAGE() {
    return {
      ENGLISH_LANGUAGE_CODE: 'en',
      SPANISH_LANGUAGE_CODE: 'es',
    };
  }

  static get CFA_HOME_URL() {
    return env.REACT_APP_CFA_HOME_URL;
  }

  static get PATHWAY_LOGOUT_REDIRECT() {
    return env.REACT_APP_PATHWAY_LOGOUT_REDIRECT;
  }

  static get OKTA_CLIENT_ID() {
    return env.REACT_APP_OKTA_CLIENT_ID;
  }

  static get OKTA_ISSUER() {
    return env.REACT_APP_OKTA_ISSUER;
  }

  static get REDUX_DEVTOOLS_TRACING_ENABLED() {
    return env.REACT_APP_ENABLE_REDUX_DEVTOOLS_TRACING_ENABLED;
  }

  static get ROUTE_LOGGING_ENABLED() {
    return env.REACT_APP_ROUTE_LOGGING_ENABLED;
  }

  static get IS_IN_JEST_TEST() {
    return import.meta.env.JEST_WORKER_ID !== undefined;
  }

  static readonly TOAST_DISPLAY_TIME = 4000;

  static readonly IS_IN_CYPRESS_TEST = !!window.Cypress as boolean;

  static get IS_IN_CYPRESS_TEST_LOCAL() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return !!window.Cypress && import.meta.env.DEV;
  }

  static get CYPRESS_URL_PARAMS() {
    return {
      IS_MOBILE_URL_PARAM: new URLSearchParams(window.location.search).get(
        'mobile',
      ),
    };
  }

  static readonly API_ERROR_CODES = {
    RESOURCE_NOT_FOUND: 'E001',
    ERROR_LOADING_RESOURCE: 'E002',
  } as const;

  static readonly DOCUMENT_TYPES = {
    GAME: 'Game',
    PROCEDURE: 'Procedure',
    TRIDION: 'Tridion',
    VIDEO: 'Video',
    XYLEME: 'Xyleme',
  } as const;

  static readonly DRAGGABLE_TYPES = {
    CATEGORY: 'CATEGORY',
    RECOMMENDED: 'RECOMMENDED',
    SUBCATEGORY: 'SUBCATEGORY',
    SECTION: 'SECTION',
    STEP: 'STEP',
    STEP_IN_SECTION: 'STEP_IN_SECTION',
  } as const;

  static readonly STEP_TYPES = {
    DOCUMENT: 'document',
    QUIZ: 'quiz',
    TASK: 'task',
    BRIGHTCOVE: 'brightcove',
  } as const;

  static readonly STEP_FORMATS = {
    VIDEO: 'VIDEO',
  } as const;

  static readonly FORMS = {
    MAX_TASK_LENGTH: 250,
    MAX_SECTION_LENGTH: 250,
  } as const;

  static readonly EXPANDED_FILE_FORMAT_TYPES = {
    GAME: 'Game',
    OPERATIONAL_REQUIREMENTS_GUIDE: 'ORG',
    QUALITY_PHOTOS: 'Quality Photos',
    JOB_AID: 'Job Aid',
    BRIGHTCOVE: 'brightcove',
    SCORM: 'SCORM',
  } as const;

  static readonly SIDEBAR = {
    EXPLORE: 'Explore',
    SETTINGS: 'Settings',
    TRAINING: 'Training',
    ADMIN: 'Admin',
  } as const;

  static readonly DUEDATE_COLORS = {
    DARK_GREEN: 'darkGreen',
    PURPLE: 'purple',
    TAN: 'tan',
  } as const;

  static readonly UUID_REGEX_MATCH =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/g;

  //TODO: We should dedupe this
  static get ADMIN_MANAGING_COUNTRIES() {
    return {
      US: {
        ID: 1,
        LABEL_KEY: 'US',
      },
      PR: {
        ID: 2,
        LABEL_KEY: 'PR',
      },
      CA: {
        ID: 3,
        LABEL_KEY: 'CA',
      },
      GB: {
        ID: 4,
        LABEL_KEY: 'GB',
      },
      SG: {
        ID: 5,
        LABEL_KEY: 'SG',
      },
    };
  }

  static get SUPPORTED_COUNTRIES() {
    return {
      CA: {
        id: 'CA',
      },
      GB: {
        id: 'GB',
        label: 'UK',
      },
      PR: {
        id: 'PR',
      },
      US: {
        id: 'US',
      },
      SG: {
        id: 'SG',
      },
    };
  }

  static get SUPPORT_LINK() {
    return env.REACT_APP_SUPPORT_LINK;
  }

  static get RESTAURANT_CONTENT_TYPES() {
    return {
      BAY_CENTER: 'BAY_CENTER',
      CFA_SUPPLY: 'CFA_SUPPLY',
      FRANCHISEE: 'FRANCHISEE',
      LBM: 'LBM',
      OFF_MENU: 'OFF-MENU',
      STC: 'STC',
      TEST: 'TEST',
      TG: 'TG',
    };
  }

  static get PRIMARY_ORDER_TAGS() {
    return ['CFA', 'LBM', 'TG'];
  }

  static get SECONDARY_ORDER_TAGS() {
    return ['FRA', 'LIC', 'COR'];
  }

  static readonly DEFAULT_PLACEHOLDER_ICON = 'Chickfila';

  static readonly DEFAULT_NATIVE_MENU_OPTIONS = { menu: false } as const;

  static get REPORT_TABLE() {
    return {
      COMPLETED: t('Generic.completed'),
      COMPLETION_DATE: t('Generic.completionDate'),
      COMPLIANCE_PLAN_NAME: t('Generic.compliancePlanName'),
      DATE_OF_BIRTH: t('Generic.dateOfBirth'),
      EXPIRATION_DATE: t('Generic.expirationDate'),
      ITEMS_COMPLETED: t('Generic.itemsCompleted'),
      NAME: t('Generic.name'),
      PLAN: t('Generic.plan'),
      STATUS: t('Generic.status'),
      TOTAL_TIME_SPENT_ON_PLAN: t('Generic.totalTimeSpentOnPlan'),
    };
  }

  static get TABLE_HEAD() {
    return [
      [
        t('Generic.plan'),
        t('Generic.itemsCompleted'),
        t('Generic.totalTimeSpentOnPlan'),
        t('Generic.status'),
        t('Generic.completionDate'),
      ],
    ];
  }

  static get COMPLIANCE_TABLE_HEAD() {
    return [
      [
        t('Generic.compliancePlanName'),
        t('Generic.totalTimeSpentOnPlan'),
        t('Generic.completionDate'),
        t('Generic.expirationDate'),
      ],
    ];
  }

  static readonly MAX_SEARCH_RESULTS = 100;

  static readonly FOOD_SAFETY_COURSE_NAME = 'Food Safety';

  static get DATADOG_APP_ID() {
    return env.REACT_APP_DATADOG_APP_ID;
  }

  static get DATADOG_CLIENT_TOKEN() {
    return env.REACT_APP_PUBLIC_DATADOG_CLIENT_TOKEN;
  }

  static get DATADOG_SERVICE() {
    if (this.IS_VERCEL_DEPLOYMENT) {
      return 'pathway-web-preview-build';
    }
    return 'pathway-web';
  }

  static get IS_LOCAL_DEPLOYMENT() {
    return (
      window.location.href.includes('localhost') ||
      window.location.href.includes('local.pathway-dev.cfadevelop.com')
    );
  }

  static get IS_VERCEL_DEPLOYMENT() {
    return window.location.href.includes('vercel-builds');
  }

  static get CATEGORY_SECTION_NAME() {
    return {
      FEATURED: 'Featured',
      CATEGORIES: 'Categories',
    };
  }

  static get PLAN_OWNER_TYPE() {
    return {
      OPERATOR: 'OPERATOR',
      LOCATION: 'LOCATION',
      SUPPORT_CENTER: 'SUPPORT_CENTER',
    };
  }

  static readonly LOGIN_CALLBACK = '/login/callback';

  static readonly SUPPORT_STAFF_LOCATION = '00000';

  static get HEIGHT() {
    return {
      MOBILE_TOP_NAV: '62px',
      MOBILE_TOP_NAV_AND_TABS: '139px',
      DESKTOP_TOP_NAV: '120px',
      DESKTOP_HEADER: '104px',
      SEARCHBAR_SUBHEADER_HEIGHT: '80px',
      TAB_HEIGHT: '72px',
      BUILD_PLANS_HEADER: '80px',
      STICKY_MENU_MARGIN: '16px',
      FOOTER: '112px',
    };
  }
}

export { routes } from './routes';
